/* =========================
   TOKEN & SESSION
========================= */

export function logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    try {
        sessionStorage.removeItem("reminder_alert_session_start");
    } catch {
        /* ignore */
    }
    window.location.replace("/login");
}

export function getToken() {
    return localStorage.getItem("token");
}

export function isTokenExpired(token) {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.exp * 1000 < Date.now();
    } catch {
        return true;
    }
}

export function isAuthenticated() {
    const token = getToken();
    if (!token) return false;

    if (isTokenExpired(token)) {
        logout();
        return false;
    }
    return true;
}

/* =========================
   USER / ROLE / PERMISSIONS
========================= */

export function getUser() {
    try {
        return JSON.parse(localStorage.getItem("user"));
    } catch {
        return null;
    }
}

export function getRoles() {
    return getUser()?.roles || [];
}

export function getPermissions() {
    return getUser()?.permissions || [];
}

export function hasRole(role) {
    return getRoles().includes(role);
}

export function hasAnyRole(roles = []) {
    return roles.some(r => getRoles().includes(r));
}

export const SUPERADMIN_ROLE_ACCESS_MSG =
    "Superadmin role cannot be created or updated. You don't have access to perform this action. Contact your owner.";

export function isSuperAdminUser(user = getUser()) {
    return (user?.roles || []).some(
        (r) => (typeof r === "string" ? r : r?.name)?.toLowerCase() === "superadmin"
    );
}

export function isSuperAdminRoleName(name) {
    return String(name || "").trim().toLowerCase() === "superadmin";
}

/* =========================
   PERMISSION HELPERS
========================= */

export function hasPermission(permission) {
    const permissions = getPermissions();
    return (
        permissions.includes(permission) ||
        permissions.includes("all:*")
    );
}

export function hasAnyPermission(permissionList = []) {
    const permissions = getPermissions();
    return permissionList.some(p =>
        permissions.includes(p) || permissions.includes("all:*")
    );
}

export function hasModuleAccess(module) {
    const permissions = getPermissions();
    return permissions.some(p => p.startsWith(`${module}:`)) ||
        permissions.includes("all:*");
}

/** Shorthand helpers for module CRUD / action permissions */
export function canReadModule(module) {
    return hasPermission(`${module}:read`);
}

export function canCreateModule(module) {
    return hasPermission(`${module}:create`);
}

export function canUpdateModule(module) {
    return hasPermission(`${module}:update`);
}

export function canDeleteModule(module) {
    return hasPermission(`${module}:delete`);
}

export function canActionModule(module) {
    return hasPermission(`${module}:action`);
}